import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ArrowLeft, Loader2, Plus, Eye, Send, FileText } from "lucide-react";

const API = "/api";

const DOC_TYPES = [
  { id: "terms", label: "Términos y Condiciones" },
  { id: "privacy", label: "Política de Privacidad" },
  { id: "complaints", label: "Libro de Reclamaciones" },
];
const LOCALES = ["es", "en", "pt", "fr"];

const STATUS_STYLE = {
  published: "text-[#5BC0FF]",
  draft: "text-amber-400",
  archived: "text-slate-500",
};

export default function AdminLegalDocuments() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [docType, setDocType] = useState("");
  const [locale, setLocale] = useState("");
  const [preview, setPreview] = useState(null);
  const [draft, setDraft] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/legal/documents`, {
        params: { doc_type: docType || undefined, locale: locale || undefined },
      });
      setDocs(r.data.documents || []);
    } catch (_e) {
      toast.error("Error al cargar documentos");
    } finally { setLoading(false); }
  }, [docType, locale]);

  useEffect(() => { load(); }, [load]);

  const openPreview = async (doc) => {
    try {
      const r = await axios.get(`${API}/admin/legal/documents/${doc.id}`);
      setPreview(r.data);
    } catch (_e) { toast.error("Error al abrir el documento"); }
  };

  const saveDraft = async () => {
    setWorking(true);
    try {
      if (draft.id) {
        await axios.put(`${API}/admin/legal/documents/${draft.id}`, {
          body: draft.body, effective_date: draft.effective_date || null,
        });
      } else {
        await axios.post(`${API}/admin/legal/documents`, {
          doc_type: draft.doc_type, locale: draft.locale, body: draft.body,
          effective_date: draft.effective_date || null,
        });
      }
      toast.success("Borrador guardado");
      setDraft(null);
      load();
    } catch (err) {
      toast.error(`Error: ${err?.response?.data?.detail || "guardar"}`);
    } finally { setWorking(false); }
  };

  const publish = async (doc) => {
    if (!window.confirm("¿Publicar esta versión? La anterior queda archivada.")) return;
    setWorking(true);
    try {
      await axios.post(`${API}/admin/legal/documents/${doc.id}/publish`);
      toast.success("Versión publicada");
      load();
    } catch (err) {
      toast.error(`Error: ${err?.response?.data?.detail || "publicar"}`);
    } finally { setWorking(false); }
  };

  return (
    <div className="min-h-screen bg-[#071B2F] pb-16">
      <div className="max-w-5xl mx-auto px-4 py-8">
        <Link to="/admin" className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-6"
              data-testid="admin-legal-back">
          <ArrowLeft className="w-4 h-4" aria-hidden="true" /> Volver al admin
        </Link>

        <h1 className="text-3xl font-bold text-white flex items-center gap-3 mb-2">
          <FileText className="w-7 h-7 text-[#5BC0FF]" aria-hidden="true" /> Documentos legales
        </h1>
        <p className="text-slate-400 text-sm md:text-base mb-6">
          Versionado inmutable. Los borradores nunca son públicos y publicar archiva la versión anterior.
        </p>

        <div className="flex flex-wrap gap-3 mb-6">
          <select value={docType} onChange={(e) => setDocType(e.target.value)}
                  aria-label="Filtrar por tipo"
                  className="bg-[#0B2545] border border-white/15 text-white rounded-lg px-3 py-2 text-sm"
                  data-testid="admin-legal-type-filter">
            <option value="">Todos los tipos</option>
            {DOC_TYPES.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
          </select>
          <select value={locale} onChange={(e) => setLocale(e.target.value)}
                  aria-label="Filtrar por idioma"
                  className="bg-[#0B2545] border border-white/15 text-white rounded-lg px-3 py-2 text-sm"
                  data-testid="admin-legal-locale-filter">
            <option value="">Todos los idiomas</option>
            {LOCALES.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
          <Button onClick={() => setDraft({ doc_type: "terms", locale: "es", body: "", effective_date: "" })}
                  className="bg-[#1B84FF] hover:bg-[#0F6FDB] text-slate-950 font-semibold"
                  data-testid="admin-legal-new">
            <Plus className="w-4 h-4 mr-1" aria-hidden="true" /> Nuevo borrador
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#5BC0FF]" /></div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-white/10">
            <table className="w-full text-sm" data-testid="admin-legal-table">
              <caption className="sr-only">Versiones de documentos legales</caption>
              <thead className="bg-[#0B2545] text-slate-400">
                <tr>
                  <th scope="col" className="text-left p-3">Tipo</th>
                  <th scope="col" className="text-left p-3">Idioma</th>
                  <th scope="col" className="text-left p-3">Versión</th>
                  <th scope="col" className="text-left p-3">Estado</th>
                  <th scope="col" className="text-left p-3">Vigencia</th>
                  <th scope="col" className="text-left p-3">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {docs.map((d) => (
                  <tr key={d.id} className="border-t border-white/10 text-slate-200"
                      data-testid={`admin-legal-row-${d.id}`}>
                    <td className="p-3">{d.doc_type}</td>
                    <td className="p-3">{d.locale}</td>
                    <td className="p-3">v{d.version}</td>
                    <td className={`p-3 ${STATUS_STYLE[d.status] || ""}`}>{d.status}</td>
                    <td className="p-3">{(d.effective_date || "").slice(0, 10) || "—"}</td>
                    <td className="p-3">
                      <div className="flex gap-2">
                        <button onClick={() => openPreview(d)} aria-label="Previsualizar"
                                className="p-1.5 rounded hover:bg-white/10 text-slate-300"
                                data-testid={`admin-legal-preview-${d.id}`}>
                          <Eye className="w-4 h-4" aria-hidden="true" />
                        </button>
                        {d.status === "draft" && (
                          <button onClick={() => publish(d)} aria-label="Publicar"
                                  className="p-1.5 rounded hover:bg-[#1B84FF]/10 text-[#5BC0FF]"
                                  data-testid={`admin-legal-publish-${d.id}`}>
                            <Send className="w-4 h-4" aria-hidden="true" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {docs.length === 0 && (
                  <tr><td colSpan={6} className="p-6 text-center text-slate-500"
                          data-testid="admin-legal-empty">Sin documentos registrados.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {preview && (
          <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4"
               role="dialog" aria-modal="true" aria-label="Previsualizar documento"
               data-testid="admin-legal-preview-modal">
            <div className="bg-[#0B2545] border border-white/15 rounded-t-2xl sm:rounded-2xl w-full max-w-2xl max-h-[88vh] flex flex-col">
              <div className="p-5 border-b border-white/10 flex items-center justify-between">
                <h2 className="text-base md:text-lg font-semibold text-white">
                  {preview.doc_type} · {preview.locale} · v{preview.version} ({preview.status})
                </h2>
                <button onClick={() => setPreview(null)} aria-label="Cerrar"
                        className="text-slate-400 hover:text-white"
                        data-testid="admin-legal-preview-close">✕</button>
              </div>
              <div className="p-5 overflow-y-auto text-sm text-slate-200 whitespace-pre-wrap flex-1">
                {preview.body || "(vacío)"}
              </div>
              <div className="p-5 border-t border-white/10 flex gap-3">
                {preview.status === "draft" && (
                  <Button onClick={() => setDraft({ ...preview })} variant="ghost" className="text-slate-300"
                          data-testid="admin-legal-preview-edit">Editar borrador</Button>
                )}
              </div>
            </div>
          </div>
        )}

        {draft && (
          <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4"
               role="dialog" aria-modal="true" aria-label="Editar borrador legal"
               data-testid="admin-legal-draft-modal">
            <div className="bg-[#0B2545] border border-white/15 rounded-t-2xl sm:rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-5">
              <h2 className="text-lg font-semibold text-white mb-4">
                {draft.id ? `Editar borrador v${draft.version}` : "Nuevo borrador"}
              </h2>
              {!draft.id && (
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div>
                    <Label htmlFor="draft-type" className="text-slate-300 text-sm">Tipo</Label>
                    <select id="draft-type" value={draft.doc_type}
                            onChange={(e) => setDraft({ ...draft, doc_type: e.target.value })}
                            className="w-full bg-[#14375F] border border-white/15 text-white rounded-lg px-3 py-2 text-sm"
                            data-testid="admin-legal-draft-type">
                      {DOC_TYPES.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="draft-locale" className="text-slate-300 text-sm">Idioma</Label>
                    <select id="draft-locale" value={draft.locale}
                            onChange={(e) => setDraft({ ...draft, locale: e.target.value })}
                            className="w-full bg-[#14375F] border border-white/15 text-white rounded-lg px-3 py-2 text-sm"
                            data-testid="admin-legal-draft-locale">
                      {LOCALES.map((l) => <option key={l} value={l}>{l}</option>)}
                    </select>
                  </div>
                </div>
              )}
              <div className="mb-3">
                <Label htmlFor="draft-date" className="text-slate-300 text-sm">Fecha de vigencia (ISO, opcional)</Label>
                <Input id="draft-date" value={draft.effective_date || ""}
                       onChange={(e) => setDraft({ ...draft, effective_date: e.target.value })}
                       className="bg-[#14375F] border-white/15 text-white"
                       data-testid="admin-legal-draft-date" />
              </div>
              <div>
                <Label htmlFor="draft-body" className="text-slate-300 text-sm">Contenido</Label>
                <textarea id="draft-body" rows={14} value={draft.body || ""}
                          onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                          className="w-full mt-1 bg-[#14375F] border border-white/15 text-white rounded-lg px-3 py-2 text-sm font-mono"
                          data-testid="admin-legal-draft-body" />
              </div>
              <div className="flex gap-3 mt-5">
                <Button onClick={saveDraft} disabled={working || !draft.body}
                        className="bg-[#1B84FF] hover:bg-[#0F6FDB] text-slate-950 font-semibold"
                        data-testid="admin-legal-draft-save">
                  {working ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : "Guardar borrador"}
                </Button>
                <Button variant="ghost" onClick={() => setDraft(null)} className="text-slate-300"
                        data-testid="admin-legal-draft-cancel">Cancelar</Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
